import { useEffect, useState, useCallback } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const STYLE = {
  available: { fill: "#171310", stroke: "rgba(224,166,60,0.45)", hover: "#E0A63C", cursor: "pointer" },
  pending: { fill: "#5a4111", stroke: "#E0A63C", hover: "#5a4111", cursor: "not-allowed" },
  claimed: { fill: "#1c1c1c", stroke: "#333", hover: "#1c1c1c", cursor: "not-allowed" },
};

const LEGEND = [
  { key: "available", label: "Available", swatch: "#171310", border: "rgba(224,166,60,0.6)" },
  { key: "pending", label: "Reserved", swatch: "#5a4111", border: "#E0A63C" },
  { key: "claimed", label: "Claimed", swatch: "#1c1c1c", border: "#444" },
];

export const TerritoryMap = () => {
  const [territories, setTerritories] = useState([]);
  const [tooltip, setTooltip] = useState(null);
  const [claim, setClaim] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/territories");
      setTerritories(data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const byName = Object.fromEntries(territories.map((t) => [t.state, t]));
  const counts = territories.reduce((a, t) => ({ ...a, [t.status]: (a[t.status] || 0) + 1 }), {});

  const onStateClick = (name) => {
    const t = byName[name];
    if (!t || t.status !== "available") return;
    setClaim(t);
  };

  return (
    <section id="territories" className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-10">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-end mb-14">
        <div className="lg:col-span-7">
          <p className="text-xs uppercase tracking-[0.3em] text-oed-gold mb-6">Claim Your Territory</p>
          <h2 className="font-display font-bold tracking-tight text-4xl md:text-5xl leading-[1.05]">
            Own an exclusive dealership territory.
          </h2>
        </div>
        <div className="lg:col-span-5">
          <p className="text-base md:text-lg text-white/60 leading-relaxed">
            Explore the map and reserve your state. Territories are granted exclusively — once a region
            is claimed, it's off the map for good. Tap an available state to stake your claim.
          </p>
        </div>
      </div>

      <div className="border border-white/10 bg-oed-surface">
        <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-4 border-b border-white/10">
          <div className="flex flex-wrap gap-6">
            {LEGEND.map((l) => (
              <div key={l.key} className="flex items-center gap-2.5">
                <span className="w-4 h-4 border" style={{ background: l.swatch, borderColor: l.border }} />
                <span className="text-xs uppercase tracking-[0.15em] text-white/60">
                  {l.label} <span className="text-white/30">({counts[l.key] || 0})</span>
                </span>
              </div>
            ))}
          </div>
          <span className="text-[11px] uppercase tracking-[0.2em] text-white/30 hidden sm:block">50 U.S. states</span>
        </div>

        <div
          className="relative"
          onMouseMove={(e) => setTooltip((t) => (t ? { ...t, x: e.clientX, y: e.clientY } : t))}
          data-testid="territory-map"
        >
          <ComposableMap projection="geoAlbersUsa" width={975} height={560} style={{ width: "100%", height: "auto" }}>
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const name = geo.properties.name;
                  const t = byName[name];
                  const status = t ? t.status : "available";
                  const s = STYLE[status];
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      onClick={() => onStateClick(name)}
                      onMouseEnter={(e) => setTooltip({ name, status, x: e.clientX, y: e.clientY })}
                      onMouseLeave={() => setTooltip(null)}
                      data-testid={t ? `territory-${t.code}` : undefined}
                      style={{
                        default: { fill: s.fill, stroke: s.stroke, strokeWidth: 0.75, outline: "none", transition: "fill 0.25s" },
                        hover: { fill: s.hover, stroke: s.stroke, strokeWidth: 0.75, outline: "none", cursor: s.cursor },
                        pressed: { fill: s.hover, outline: "none" },
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ComposableMap>

          {tooltip && (
            <div
              className="pointer-events-none fixed z-[60] bg-black/90 border border-white/15 px-3 py-2 backdrop-blur-md"
              style={{ left: tooltip.x + 14, top: tooltip.y + 14 }}
            >
              <p className="font-display font-bold text-sm tracking-tight">{tooltip.name}</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-oed-gold">{tooltip.status}</p>
            </div>
          )}
        </div>
      </div>

      <ClaimModal territory={claim} onClose={() => setClaim(null)} onClaimed={() => { setClaim(null); load(); }} />
    </section>
  );
};

const ClaimModal = ({ territory, onClose, onClaimed }) => {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setForm({ full_name: "", email: "", phone: "", message: "" }); }, [territory]);

  const valid = form.full_name.trim() && /\S+@\S+\.\S+/.test(form.email) && form.phone.trim();

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/territories/claim", { state_code: territory.code, ...form });
      toast.success(`${territory.state} reserved. Our team will contact you to finalize your territory.`);
      onClaimed();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {territory && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-xl"
          onClick={onClose}
          data-testid="claim-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-oed-surface border border-white/10 p-8 md:p-10"
          >
            <button data-testid="claim-close" onClick={onClose} className="absolute top-5 right-5 text-white/50 hover:text-white"><X size={22} /></button>
            <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-oed-gold">
              <MapPin size={14} /> Claim Territory
            </p>
            <h3 className="mt-3 font-display font-bold text-3xl tracking-tight">{territory.state}</h3>
            <p className="mt-2 text-sm text-white/50">Reserve exclusive dealership rights for this state.</p>

            <div className="mt-8 space-y-6">
              {[
                { k: "full_name", label: "Full Name", ph: "Jane Doe", type: "text" },
                { k: "email", label: "Email", ph: "you@email.com", type: "email" },
                { k: "phone", label: "Phone", ph: "(256) 268-7146", type: "tel" },
              ].map((f) => (
                <label key={f.k} className="block">
                  <span className="text-[11px] uppercase tracking-[0.2em] text-white/40">{f.label}</span>
                  <input
                    data-testid={`claim-input-${f.k}`}
                    type={f.type} value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                    placeholder={f.ph}
                    className="mt-2 w-full bg-transparent border-b border-white/20 py-3 text-white placeholder-white/25 focus:border-oed-gold focus:outline-none transition-colors"
                  />
                </label>
              ))}
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.2em] text-white/40">Message (optional)</span>
                <textarea
                  data-testid="claim-input-message"
                  rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell us about your plans…"
                  className="mt-2 w-full bg-transparent border-b border-white/20 py-3 text-white placeholder-white/25 focus:border-oed-gold focus:outline-none resize-none transition-colors"
                />
              </label>
            </div>

            <button
              data-testid="claim-submit"
              onClick={submit} disabled={!valid || submitting}
              className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-oed-gold text-black py-4 text-sm font-medium hover:bg-oed-goldHover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? "Reserving…" : <>Reserve {territory.state} <Check size={16} /></>}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
